import { ButtonNew, LuneThemeNew, Text } from '@lune-fe/lune-ui-lib'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import Box from '@mui/material/Box'
import MuiChip from '@mui/material/Chip'
import { Dispatch, SetStateAction } from 'react'

const { palette } = LuneThemeNew

export interface ActiveFilterConfig<T> {
    /**
     * Display name for the filter category
     */
    filterName: string
    /**
     * Currently selected values
     */
    selectedValues: T[]
    /**
     * Setter function to update selected values
     */
    setSelectedValues: Dispatch<SetStateAction<T[]>>
    /**
     * Optional function to transform values for display
     */
    transformLabel?: (value: T) => string
}

export interface ActiveFiltersChipsProps<T = string> {
    /**
     * Array of filter configurations
     * T can be a union type like (string | EstimateTypeEnum) to support mixed types
     */
    filters: ActiveFilterConfig<T>[]
    /**
     * Optional callback when all filters are cleared
     */
    onClearAll?: () => void
}

const ActiveFiltersChips = <T,>({ filters, onClearAll }: ActiveFiltersChipsProps<T>) => {
    const { spacing } = LuneThemeNew
    const hasActiveFilters = filters.some((filter) => filter.selectedValues.length > 0)

    if (!hasActiveFilters) {
        return null
    }

    const handleRemoveValue = <T,>(filter: ActiveFilterConfig<T>, valueToRemove: T) => {
        const newValues = filter.selectedValues.filter((value) => value !== valueToRemove)
        filter.setSelectedValues(newValues)
    }

    const handleClearAll = () => {
        filters.forEach((filter) => {
            filter.setSelectedValues([])
        })
        onClearAll?.()
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing(1),
                px: spacing(3),
                pb: spacing(1),
                flexWrap: 'wrap',
                // 4 * 30 (chips) + 3 * 8 (gaps) + 8 container padding bottom = 152
                maxHeight: '152px',
                overflowY: 'auto',
                flex: '1 0 auto',
            }}
        >
            {filters.map((filter, filterIndex) =>
                filter.selectedValues.map((value, valueIndex) => {
                    const label = filter.transformLabel
                        ? filter.transformLabel(value)
                        : String(value)

                    return (
                        <MuiChip
                            key={`${filterIndex}-${valueIndex}-${String(value)}`}
                            label={
                                <Text
                                    variant={'body3'}
                                    sx={{
                                        color: palette.TextSecondary,
                                    }}
                                >
                                    <span style={{ textTransform: 'capitalize' }}>
                                        {filter.filterName}:{' '}
                                    </span>
                                    {label}
                                </Text>
                            }
                            onDelete={() => handleRemoveValue(filter, value)}
                            deleteIcon={
                                <CloseOutlinedIcon
                                    sx={{
                                        marginRight: '8px !important',
                                        color: palette.TextSecondary,
                                        '&:hover': {
                                            color: `${palette.TextPrimary} !important`,
                                        },
                                    }}
                                />
                            }
                            size="small"
                            sx={{
                                height: '30px',
                                py: '3px',
                                border: '1px solid',
                                borderColor: palette.Surface700,
                                backgroundColor: palette.Surface500,
                                borderRadius: '100px',
                                display: 'flex',
                                '&:hover': {
                                    backgroundColor: palette.Surface600,
                                    svg: {
                                        color: palette.TextPrimary,
                                    },
                                },
                            }}
                        />
                    )
                }),
            )}
            <ButtonNew
                leadingIcon={<CloseOutlinedIcon />}
                size={'sm'}
                style={'floating'}
                onClick={handleClearAll}
            >
                Clear filters
            </ButtonNew>
        </Box>
    )
}

export default ActiveFiltersChips
